import { CloseOutlined } from "@ant-design/icons"
import { Button, DatePicker, Flex, Input, Popover, Select } from "antd"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import UserRoleDropdown from "../../UserRoleDropdown/index.js"
import { resolveFieldTranslation } from "../../../utils/formTranslations.js"
import { getWorkflowStatusOptions } from "../../../utils/workflowHelper.js"
import dayjs from "dayjs"
import { toDatePickerValue } from "../../../utils/safeDate.js"

const itemsNotToAllow = ["addressField", "attachment", "imageForm", "linkField", "contactField", "infoForm", "tableForm", "textAreaForm", "timeForm"]
const defaultCols = ["createdOn", "createdBy", "currentCycle", "caseId", "status"]
const dropdownFields = ["checkboxField", "dropdownField", "multipleChoiceField", "radioField"]

const SearchContainer = (props) => {
    const { t, i18n } = useTranslation();
    const { workflow, definedWidgets = [], userInfo, searchParams, updateSearchParams, getSearchResult, resetSearch, selectedView } = props
    const [searchFields, setSearchFields] = useState([])

    useEffect(() => {
        if (definedWidgets?.length > 0) {
            let reqFields = definedWidgets.filter(widget => widget && !itemsNotToAllow.includes(widget.formType) && typeof widget.id === "string" && !widget.id.includes("::"));

            selectedView?.selectedColumns?.map(column => {
                let colIsDefinedWidget = definedWidgets.find(widget => widget.id === column.id)
                if (!colIsDefinedWidget) {
                    if (defaultCols.includes(column.id)) {
                        if (column.id === "status") {
                            reqFields.unshift({ ...column, options: getWorkflowStatusOptions(workflow, { language: i18n.language, t }), formType: 'dropdownField' })
                        }
                        else if (column) {
                            let type = 'inputForm'
                            if (column.id === "createdBy") {
                                type = "userForm"
                            } else if (column.id === "createdOn") {
                                type = "dateForm"
                            }
                            reqFields.unshift({ ...column, formType: type })
                        }
                    }
                }
            })
            reqFields = reqFields.map(field => {
                if(field.formType === "switchForm") {
                    return {...field, options: [{label: t('Yes'), value: "true"}, {label: t('No'), value: "false"}], formType: "dropdownField"}
                }
                if (field && dropdownFields.includes(field.formType)) {
                    return { ...field, formType: 'dropdownField' }
                }
                return field;
            })
            reqFields.sort((a, b) => (a?.label || "").localeCompare(b?.label || "", undefined, { sensitivity: "base" }))
            setSearchFields(reqFields)
        }
        // i18n.language is a dependency because the status options are localised
        // when they are built, unlike widget labels which resolve at render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedView, definedWidgets, i18n.language])

    function localDateToUTC(dateStr) {
        // dateStr = "YYYY-MM-DD"
        const [year, month, day] = dateStr.split('-').map(Number);

        // Create local date at midnight
        const localDate = new Date(year, month - 1, day, 0, 0, 0);

        // Convert to UTC ISO
        return localDate.toISOString();
    }

    const handleSearchChange = (id, value, isDate = false) => {
        if (isDate && value && id !== "createdOn") {
            const localDate = localDateToUTC(value);
            updateSearchParams(id, localDate)
            return
        }
        updateSearchParams(id, value)
    }

    const onSelect = (id, selectedData) => {
        updateSearchParams(id, selectedData?.user || [])
    }

    const getSearchField = (fieldInfo) => {
        let fieldType = fieldInfo.formType

        let component = null
        switch (fieldType) {
            case ("dropdownField"):
                component = <Select mode='multiple' style={{ width: "100%" }} options={fieldInfo.options} onChange={(value) => handleSearchChange(fieldInfo.id, value)} value={searchParams?.[fieldInfo.id] || undefined} />
                break;
            case ("userForm"):
                component = <UserRoleDropdown
                    tabsRequired={['user']}
                    getSpaceUsers={false}
                    selectedOptions={searchParams?.[fieldInfo.id] ? {
                        "user": searchParams?.[fieldInfo.id] || [],
                        "role": [],
                        "group": []
                    } : { user: [], role: [], group: [] }}
                    onSelect={(selectedData) => {
                        onSelect(fieldInfo.id, selectedData)
                    }}
                />
                break;
            case ("dateForm"):
                component = <DatePicker size="large" style={{ width: "100%" }} format='YYYY-MM-DD' defaultValue={toDatePickerValue(searchParams?.[fieldInfo.id])} onChange={(_date, dateString) => handleSearchChange(fieldInfo.id, dateString, true)} />
                break;
            case ("inputForm"):
                component = <Input onChange={(e) => handleSearchChange(fieldInfo.id, e.target.value)} value={searchParams?.[fieldInfo.id] || ""} />
                break;
            default:
                component = <Input onChange={(e) => handleSearchChange(fieldInfo.id, e.target.value)} value={searchParams?.[fieldInfo.id] || ""} />
        }
        return component;
    }

    return (
        <div className="filters-section">
            <div className="filters-block ">
                {/* <div className="workflow-step" style={{marginBottom: "16px"}}>
                    <div className="search-result-title">{t("Created by")}</div>
                        <UserRoleDropdown
                            tabsRequired={['user']}
                            getSpaceUsers={false}
                            selectedOptions={searchParams.createdBy ? {
                      "user": searchParams.createdBy || [],
                      "role": [],
                      "group":[]
                    } : { user: [], role: [], group: [] }}
                    onSelect={onSelect}
                    />
                </div> */}
                {
                    searchFields.map((field, index) => {
                        // Show the field label (and any option labels) in the
                        // viewer's language; the field id used for filtering is
                        // unchanged so search logic is unaffected.
                        const localizedField = resolveFieldTranslation(field, i18n.language)
                        return (
                            <div key={"field " + index} >
                                <div className="mb-1">{localizedField.label}</div>
                                <div>{getSearchField(localizedField)}</div>
                            </div>
                        )
                    })
                }
            </div>

            <Flex justify="flex-end" align="center" gap={8} style={{ marginTop: "16px" }}>
                <Button style={{ border: "none", color: "grey" }} onClick={resetSearch}>{t("Reset")}</Button>
                <Button type="primary" onClick={() => getSearchResult()}>{t("Apply")}</Button>
            </Flex>
        </div>
    )
}

export default SearchContainer
